import React, {FC} from 'react';
import styled from "styled-components";
import RouteArrowIcon from "../../../media/icons/route_arrow.svg?react";
import {IAirport} from "../../../store/reducers/fleetReducer/fleetReducerTypes";
import {defineMessages, useIntl} from "react-intl";
import {CommonDetailsMessages} from "../../../intl/commonMessages/CommonDetailsMessages";
import {DisplayAirport} from "../../../utils/helpers/textDisplayHelpers/DisplayAirport";
import {formatDate} from "../../../utils/helpers/dateHelpers/dateHelpers";
import {DetailItem} from "../detailItem/DetailItem";
import {DisplayFlyDuration} from "../../../utils/helpers/textDisplayHelpers/DisplayFlyDuration";
import {DisplayFlyDistance} from "../../../utils/helpers/textDisplayHelpers/DisplayFlyDistance";
import {useAppSelector} from "../../../store/store";
import {DistanceUnit} from "../../../utils/constans/statuses/distanceUnits";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import {LocalTimeDisplay} from "../localTimeDisplay/LocalTimeDisplay";

dayjs.extend(utc);


const FlyRouteStyled = styled.div`
  background: #FFFFFF;
  padding: 16px 20px 16px 24px;
  display: flex;
  flex-direction: column;
  border-top: 1px solid #D9D9D9;
  border-radius: 0 0 10px 10px;

  & > div {
    :nth-child(1) {
      font-weight: 600;
      font-size: 16px;
      line-height: 22px;
      letter-spacing: -0.01em;
      color: #000000;
      margin-bottom: 8px;
    }

    :nth-child(2) {
      display: grid;
      grid-template-columns: 5fr 1fr 5fr;
      column-gap: 8px;
      margin-bottom: 16px;
      align-items: center;

      & > div {
        display: flex;
        flex-direction: column;

        & > div {
          :nth-child(1), :nth-child(3) {
            font-weight: 400;
            font-size: 14px;
            line-height: 22px;
            opacity: 0.5;
          }

          :nth-child(2) {
            font-weight: 600;
            font-size: 16px;
            line-height: 22px;
            letter-spacing: -0.01em;
            color: #000000;
          }
        }
      }
    }

    :last-child {
      display: flex;
      flex-direction: row;
      gap: 4px;

      div {
        flex-grow: 1;
      }
    }
  }
`;

const DateTimeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-end;
  
  & > div:first-child {
    font-weight: 600;
    font-size: 16px;
    line-height: 22px;
    letter-spacing: -0.01em;
    color: #000000;
  }
`;

const LocalTimeWrapper = styled.div`
  & > div {
    padding: 0 !important;
  }
`;

const AvailableDateItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-end;
  
  & > div:first-child {
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    color: #000000;
  }
`;

const AvailableDatesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 5px;
  margin-bottom: 8px;
  
  & > div {
    width: 100%;
    font-size: 14px;
    line-height: 22px;
    color: #000000;

    :nth-child(1) {
      font-weight: 600;
    }
    
    :nth-child(2) {
      border: 1px solid #D9D9D9;
      border-radius: 5px;
      font-weight: 400;
      display: flex;
      column-gap: 8px;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      padding: 11px 10px 9px 10px;
    }
  }
`


const FlyRouteMessages = defineMessages({
  title: {
    id: "flyRoute.title",
    defaultMessage: "Route"
  },
  positioningRoute: {
    id: "flyRoute.positioningRoute",
    defaultMessage: "Positioning Route"
  }
})

interface IFlyRoute {
  index?: number;
  airportFrom: IAirport;
  airportTo: IAirport;
  dateFrom?: Date;
  dateTo?: Date;
  availableFromDate?: Date;
  availableToDate?: Date;
  flyHoursDuration: number;
  flyMinutesDuration: number;
  distance: number;
  pax: number;
  technicalStopHoursDuration?: number;
  technicalStopMinutesDuration?: number;
  timezoneOffset?: number;
  isPositioning?: boolean;
  titleAction?: React.ReactNode;
}

export const FlyRoute: FC<IFlyRoute> = ({
                                          index = 0,
                                          airportFrom,
                                          airportTo,
                                          dateFrom,
                                          dateTo,
                                          availableFromDate,
                                          availableToDate,
                                          flyHoursDuration,
                                          flyMinutesDuration,
                                          distance,
                                          pax,
                                          technicalStopHoursDuration = 0,
                                          technicalStopMinutesDuration = 0,
                                          timezoneOffset,
                                          isPositioning = false,
                                          titleAction,
                                        }) => {
  const intl = useIntl();
  const {offset: userTimezoneOffset} = useAppSelector(state => state.user.oneItemObject.item.timezone) || {offset: 0};
  const offset = timezoneOffset ?? userTimezoneOffset;
  const userDistanceUnit = useAppSelector(state => state.user.oneItemObject.item.distanceUnit) || DistanceUnit.NAUTICAL_MILE;
  
  // Determine route title
  const routeTitle = isPositioning 
    ? intl.formatMessage(FlyRouteMessages.positioningRoute)
    : `${intl.formatMessage(FlyRouteMessages.title)} ${index + 1}`;

  const technicalStopDetailItem = technicalStopHoursDuration + technicalStopMinutesDuration > 0
    && <DetailItem label={intl.formatMessage(CommonDetailsMessages.technicalStop)} value={"1"}
                   tooltip={DisplayFlyDuration(technicalStopHoursDuration, technicalStopMinutesDuration)}/>

  const availableDatesItem = availableFromDate && availableToDate
  && <AvailableDatesWrapper>
      <div>{intl.formatMessage(CommonDetailsMessages.availableRange)}</div>
      <div>
        <AvailableDateItem>
          <div>{formatDate(availableFromDate, 0, "datetime_short_year")} UTC</div>
          <LocalTimeWrapper>
            <LocalTimeDisplay
              utcDate={dayjs.utc(availableFromDate)}
              airportOffset={airportFrom?.timezone?.offset}
            />
          </LocalTimeWrapper>
        </AvailableDateItem>
        <RouteArrowIcon />
        <AvailableDateItem>
          <div>{formatDate(availableToDate, 0, "datetime_short_year")} UTC</div>
          <LocalTimeWrapper>
            <LocalTimeDisplay
              utcDate={dayjs.utc(availableToDate)}
              airportOffset={airportTo?.timezone?.offset}
            />
          </LocalTimeWrapper>
        </AvailableDateItem>
      </div>
    </AvailableDatesWrapper>

  return (
    <FlyRouteStyled>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>{routeTitle}</span>
        {titleAction}
      </div>

      <div>
        <div>
          <div>{intl.formatMessage(CommonDetailsMessages.from)}</div>
          <div>{DisplayAirport(airportFrom, "codes")}</div>
          {dateFrom && (
            <DateTimeWrapper>
              <div>{formatDate(dateFrom, 0, "datetime_short_year")} UTC</div>
              <LocalTimeWrapper>
                <LocalTimeDisplay
                  utcDate={dayjs.utc(dateFrom)}
                  airportOffset={airportFrom?.timezone?.offset}
                />
              </LocalTimeWrapper>
            </DateTimeWrapper>
          )}
        </div>
        <RouteArrowIcon/>
        <div>
          <div>{intl.formatMessage(CommonDetailsMessages.to)}</div>
          <div>{DisplayAirport(airportTo, "codes")}</div>
          {dateTo && (
            <DateTimeWrapper>
              <div>{formatDate(dateTo, 0, "datetime_short_year")} UTC</div>
              <LocalTimeWrapper>
                <LocalTimeDisplay
                  utcDate={dayjs.utc(dateTo)}
                  airportOffset={airportTo?.timezone?.offset}
                />
              </LocalTimeWrapper>
            </DateTimeWrapper>
          )}
        </div>
      </div>

      {availableDatesItem}

      <div>
        <DetailItem label={intl.formatMessage(CommonDetailsMessages.duration)}
                    value={DisplayFlyDuration(flyHoursDuration, flyMinutesDuration)}/>
        <DetailItem label={intl.formatMessage(CommonDetailsMessages.distance)} value={DisplayFlyDistance(distance, userDistanceUnit)}/>
        {technicalStopDetailItem}
        <DetailItem label={intl.formatMessage(CommonDetailsMessages.pax)} value={pax.toString()}/>
      </div>
    </FlyRouteStyled>
  );
};
